import { DiagnosticsCenter } from './diagnostics.js';
import { evaluateConservation } from './conservation.js';
import { defaultsFor, normalizeParams } from './params.js';
import { isFiniteNumber } from './math.js';

const BASE_DT = {
  pendulum: 1 / 240,
  collision: 1 / 240,
  incline: 1 / 120,
  circular: 1 / 240,
};

const MIN_DT = 1 / 4000;

function stateIsFinite(state) {
  if (!state || typeof state !== 'object') return false;
  for (const value of Object.values(state)) {
    if (typeof value === 'number' && !isFiniteNumber(value)) return false;
  }
  return true;
}

/**
 * 仿真运行器：固定步长积分 + 参数护栏 + 守恒监测 + 运行时自愈。
 * 物理模型保持纯函数；所有“修复”动作都记录到 DiagnosticsCenter。
 */
export class SimulationRunner {
  constructor(model, options = {}) {
    this.model = model;
    this.diagnostics = options.diagnostics ?? new DiagnosticsCenter();
    this.autoRepair = options.autoRepair ?? true;
    this.speed = 1;
    this.baseDt = options.fixedDt ?? BASE_DT[model.id] ?? 1 / 240;
    this.fixedDt = this.baseDt;
    this.maxSubsteps = options.maxSubsteps ?? 40;
    this.accumulator = 0;
    this.running = false;
    this.finished = false;
    this.time = 0;
    this.repairs = 0;
    this.errorCount = 0;
    this.consecutiveErrors = 0;
    this.criticalRecoveries = 0;
    this.lastConservationCheck = 0;
    this.warnedQuantities = new Set();
    this.conservation = null;

    const initial = normalizeParams(model, { ...defaultsFor(model), ...(options.params ?? {}) });
    this.params = initial.params;
    this.#logParamRepairs(initial.repairs);
    this.state = model.createState(this.params);
    this.baseline = model.reference(this.state, this.params);
    this.conservation = evaluateConservation(model, this.params, this.state, this.baseline);
    this.diagnostics.info('仿真引擎已就绪', {
      code: 'READY',
      detail: `${model.meta?.title ?? model.id} · ${(1 / this.fixedDt).toFixed(0)} Hz 固定步长`,
      action: '参数修改将通过护栏检查后生效',
    });
  }

  #logParamRepairs(repairs) {
    for (const repair of repairs) {
      const field = this.model.schema.find((item) => item.key === repair.key);
      this.repairs += 1;
      this.diagnostics.repair(`参数「${field?.label ?? repair.key}」已自动修正`, {
        code: 'PARAM_REPAIR',
        detail: `${String(repair.from)} → ${String(repair.to)}：${repair.reason}`,
        action: '已使用安全参数继续运行',
        meta: { key: repair.key },
      });
    }
  }

  play() {
    if (this.finished) return;
    this.running = true;
  }

  pause() {
    this.running = false;
  }

  toggle() {
    if (this.running) this.pause();
    else this.play();
  }

  setSpeed(speed) {
    this.speed = Math.min(4, Math.max(0.25, Number(speed) || 1));
  }

  reset({ silent = false } = {}) {
    this.state = this.model.createState(this.params);
    this.baseline = this.model.reference(this.state, this.params);
    this.conservation = evaluateConservation(this.model, this.params, this.state, this.baseline);
    this.time = 0;
    this.accumulator = 0;
    this.finished = false;
    this.consecutiveErrors = 0;
    this.criticalRecoveries = 0;
    this.lastConservationCheck = 0;
    this.warnedQuantities.clear();
    this.fixedDt = this.baseDt;
    this.running = false;
    if (!silent) {
      this.diagnostics.info('实验已重置', {
        code: 'RESET',
        detail: '初始条件已重新载入，守恒基线同步更新',
      });
    }
  }

  /**
   * 更新参数。
   * @param {object} patch
   * @param {{reset?: boolean, silent?: boolean}} [options]
   */
  setParams(patch, { reset = false, silent = false } = {}) {
    const merged = { ...this.params, ...patch };
    const normalized = normalizeParams(this.model, merged);
    this.#logParamRepairs(normalized.repairs);
    const changed = this.model.schema.some((field) => normalized.params[field.key] !== this.params[field.key]);
    this.params = normalized.params;
    if (!changed && !reset) return false;
    this.warnedQuantities.clear();
    if (reset) {
      this.reset({ silent });
    } else {
      this.baseline = this.model.reference(this.state, this.params);
      this.conservation = evaluateConservation(this.model, this.params, this.state, this.baseline);
      this.lastConservationCheck = this.time;
    }
    return true;
  }

  /**
   * 用真实时间推进仿真。内部按固定物理步长累积，避免帧率影响结果。
   * @param {number} realSeconds
   */
  advance(realSeconds) {
    if (!this.running || this.finished) return;
    const dt = Math.min(Math.max(realSeconds, 0), 0.06);
    this.accumulator += dt * this.speed;
    let iterations = 0;
    while (this.accumulator + 1e-12 >= this.fixedDt && iterations < this.maxSubsteps) {
      this.#physicsStep(this.fixedDt);
      this.accumulator -= this.fixedDt;
      iterations += 1;
      if (this.finished || !this.running) break;
    }
    if (iterations >= this.maxSubsteps) {
      this.accumulator = 0;
      this.diagnostics.warn('计算负载过高，已丢弃部分积压时间', {
        code: 'STEP_BUDGET',
        detail: '页面渲染跟不上物理步进，已自动限制每帧最大步数',
        action: '可降低播放倍速以获得更平滑画面',
      });
    }
  }

  /** 手动单步，便于课堂逐帧讲解。 */
  stepFrame(seconds = 1 / 30) {
    const wasRunning = this.running;
    this.running = false;
    const steps = Math.max(1, Math.round(seconds / this.fixedDt));
    for (let i = 0; i < steps && !this.finished; i += 1) {
      this.#physicsStep(this.fixedDt);
    }
    return wasRunning;
  }

  #physicsStep(dt) {
    const lastGood = this.state;
    try {
      const next = this.model.advance(this.state, this.params, dt);
      if (!stateIsFinite(next)) throw new Error('模型产生了非有限状态（NaN/Infinity）');
      this.state = next;
      this.consecutiveErrors = 0;
      this.time = next.t;
    } catch (error) {
      this.errorCount += 1;
      this.consecutiveErrors += 1;
      this.diagnostics.error('物理步进异常，Agent 已捕获', {
        code: 'STEP_FAILED',
        detail: error instanceof Error ? error.message : String(error),
        action: '已回滚到上一个安全状态，并细化积分步长',
      });
      this.state = lastGood;
      this.accumulator = 0;
      this.#applyNumericalRepair('步进异常');
      if (this.consecutiveErrors >= 3) {
        this.running = false;
        this.diagnostics.error('连续异常，仿真已安全暂停', {
          code: 'STEP_FAILED_SAFE_STOP',
          detail: '自动修复未能恢复稳定步进，已停止推进以保护课堂演示',
          action: '请重置实验或检查极端参数',
        });
      }
      return;
    }

    if (this.model.isFinished?.(this.state, this.params)) {
      this.finished = true;
      this.running = false;
      this.diagnostics.info('实验数据采集完成', {
        code: 'FINISHED',
        detail: this.model.measure(this.state, this.params).status?.detail ?? '',
        action: '可导出指导书或重置后对比不同参数',
      });
      return;
    }

    if (this.time - this.lastConservationCheck >= 0.1) {
      this.#checkConservation();
      this.lastConservationCheck = this.time;
    }
  }

  #applyNumericalRepair(reason) {
    if (!this.autoRepair) return;
    this.repairs += 1;
    this.fixedDt = Math.max(MIN_DT, this.fixedDt / 2);
    this.diagnostics.repair('数值稳定性修复已执行', {
      code: 'NUMERICAL_REPAIR',
      detail: `${reason}；积分步长自动调整为 ${(1 / this.fixedDt).toFixed(0)} Hz`,
      action: '使用更小步长继续计算',
    });
  }

  #checkConservation() {
    const report = evaluateConservation(this.model, this.params, this.state, this.baseline);
    this.conservation = report;
    if (report.level === 'ok') return;

    if (report.level === 'critical') {
      if (!this.autoRepair) {
        this.diagnostics.warn('守恒量偏差超过临界值', {
          code: 'CONSERVATION_CRITICAL',
          detail: report.warnings.map((item) => item.message).join('；'),
        });
        return;
      }
      this.repairs += 1;
      this.criticalRecoveries += 1;
      this.fixedDt = Math.max(MIN_DT, this.fixedDt / 2);
      this.state = this.model.createState(this.params);
      this.time = 0;
      this.accumulator = 0;
      this.baseline = this.model.reference(this.state, this.params);
      this.lastConservationCheck = 0;
      this.conservation = evaluateConservation(this.model, this.params, this.state, this.baseline);
      this.diagnostics.repair('守恒量偏差超限，已自动回滚并细化步长', {
        code: 'CONSERVATION_RECOVERY',
        detail: report.warnings.map((item) => item.message).join('；'),
        action: `实验已回到初始状态，步长调整为 ${(1 / this.fixedDt).toFixed(0)} Hz`,
      });
      if (this.criticalRecoveries >= 3) {
        this.running = false;
        this.diagnostics.error('多次守恒修复后仍不稳定，已安全暂停', {
          code: 'CONSERVATION_SAFE_STOP',
          detail: '请减小播放倍速或使用更温和的参数',
          action: '点击重置后继续',
        });
      }
      return;
    }

    for (const warning of report.warnings) {
      if (this.warnedQuantities.has(warning.quantity)) continue;
      this.warnedQuantities.add(warning.quantity);
      this.diagnostics.warn('守恒误差接近阈值', {
        code: 'CONSERVATION_WARNING',
        detail: warning.message,
        action: '已自动减小积分步长，建议同时降低播放倍速',
      });
    }
  }

  /** Agent 自检失败后的定向修复。 */
  stabilize() {
    this.fixedDt = Math.max(MIN_DT, this.fixedDt / 2);
    this.accumulator = 0;
    this.conservation = evaluateConservation(this.model, this.params, this.state, this.baseline);
    this.diagnostics.repair('自检触发稳定性修复', {
      code: 'SELF_TEST_REPAIR',
      detail: `步长已调整为 ${(1 / this.fixedDt).toFixed(0)} Hz`,
      action: '建议重新运行自检确认',
    });
  }

  getSnapshot() {
    const metrics = this.model.measure(this.state, this.params);
    const conservation =
      this.conservation ?? evaluateConservation(this.model, this.params, this.state, this.baseline);
    return {
      experimentId: this.model.id,
      time: this.time,
      state: { ...this.state },
      params: { ...this.params },
      metrics,
      conservation,
      running: this.running,
      speed: this.speed,
      fixedDt: this.fixedDt,
      finished: this.finished,
      repairs: this.repairs,
      errors: this.errorCount,
      diagnostics: this.diagnostics.snapshot(),
    };
  }
}
